import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Truncation from '../../constants/Truncation';
import style from './style/treenode.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { unescape } from 'html-escaper';

const LevelPaddingLeft = 20;

/**
 * TreeNode (part of TreeView component)
 *
 * @example
 * <TreeNode
 *     node={ node }
 *     getChildNodes={ this.getChildNodes.bind(this) }
 *     onToggle={ this.props.onToggle }
 *     truncation: Truncation.ELLIPSIS
 *     icons={ icons }
 *     classes={ classes }
 *     selectedNodeActionComponent={ selectedNodeActionComponent }
 *     useNameInsteadOfKey={ this.props.useNameInsteadOfKey }
 *     showDeactivated={ true }
 * />
 */
export default class TreeNode extends Component {
    getNodeLabel(node) {
        if (!node?.path) {
            return '';
        }

        const pathItems = node.path.split('/');

        // this is useful in case of doubles. you still distinct with uuid but show the name for clarity
        if (this.props.useNameInsteadOfKey) {
            return unescape(node.name);
        }

        // path is structured like: /path/to/file
        return unescape(pathItems[pathItems.length - 1]);
    }

    render() {
        const {
            node,
            getChildNodes,
            level = 0,
            onToggle,
            truncation,
            icons,
            classes,
            selectedNodeActionComponent,
            showDeactivated
        } = this.props;

        let nodeIcon = null;
        let isSelected = false;
        let isDeactivated = false;

        // is there an overload for the 'closed' indicator?
        const indicatorIcon = <FontAwesomeIcon icon={ icons.indicatorIcon ? icons.indicatorIcon : 'chevron-right' } />;

        // is there an overload for the 'open' indicator provided?
        const indicatorIconOpen = <FontAwesomeIcon icon={ icons.indicatorIconOpen ? icons.indicatorIconOpen : 'chevron-down' } />;

        // is there a specific node icon set?
        if (icons.nodeIcon && icons.nodeIcon[node.type]) {
            nodeIcon = <FontAwesomeIcon icon={ icons.nodeIcon[node.type] } />;
        }

        if (node.isSelected) {
            isSelected = true;
        }

        if (node.deactivated) {
            isDeactivated = true;
        }

        // determine truncation to ellipsis or break word
        let truncationStyle = '';

        switch (truncation) {
            case Truncation.ELLIPSIS:
                truncationStyle = style.truncateLabel;
                break;
            case Truncation.BREAK_WORD:
                truncationStyle = style.breakWordLabel;
                break;
            default:
                break;
        }

        if (!showDeactivated && node.deactivated) {
            return null;
        }

        const nodeLabel = this.getNodeLabel(node);

        return (
            <div>
                <div
                    data-cy={`tree-node-${nodeLabel}-button`}
                    tabIndex="-1"
                    role="button"
                    onClick={() => {
                        onToggle(node);
                    }}
                    className={ `${style.treeNode} ${isSelected ? style.selected : ''} ${classes.treeNode}` }
                    style={{
                        paddingLeft: `${level * LevelPaddingLeft}px`
                    }}
                >
                    { (node.children && node.children.length) || node.endNode === false
                        ? <div className={ node.isOpen ? style.indicatorIcon__open : style.indicatorIcon }>
                            { node.isOpen ? indicatorIconOpen : indicatorIcon }
                        </div>
                        : null }
                    { nodeIcon ? <div className={ style.typeIcon }>
                        { nodeIcon }
                    </div> : null}
                    <span className={ `${truncationStyle} ${isDeactivated ? style.deactivated : ''}` }>
                        { nodeLabel }
                    </span>
                    { !isDeactivated && isSelected && selectedNodeActionComponent ? selectedNodeActionComponent(node) : null }
                </div>
                { node.isOpen && getChildNodes(node)?.map((childNode, index) => (
                    <TreeNode
                        key={ `childNode-${index}` }
                        { ...this.props }
                        node={ childNode }
                        level={ level + 1 }
                    />
                ))}
            </div>
        );
    }
}

TreeNode.propTypes = {
    node: PropTypes.shape({
        path: PropTypes.string.isRequired,
        type: PropTypes.string,
        isRoot: PropTypes.bool,
        children: PropTypes.array.isRequired
    }),
    getChildNodes: PropTypes.func.isRequired,
    level: PropTypes.number.isRequired,
    onToggle: PropTypes.func.isRequired,
    truncation: PropTypes.oneOf([Truncation.ELLIPSIS, Truncation.BREAK_WORD]),
    icons: PropTypes.object,
    classes: PropTypes.object,
    selectedNodeActionComponent: PropTypes.func,
    useNameInsteadOfKey: PropTypes.bool,
    showDeactivated: PropTypes.bool
};

TreeNode.defaultProps = {
    level: 0,
    useNameInsteadOfKey: false
};
